import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Trash2, DollarSign, TrendingUp, Bookmark, Compass } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Saved() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/professions/saved");
    setItems(data.saved);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (code) => {
    await api.delete(`/professions/saved/${code}`);
    setItems((prev) => prev.filter((i) => i.onet_code !== code));
    toast("Removed");
  };

  if (!items) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-brand-primary" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <span className="nb-badge bg-brand-yellow mb-2"><Bookmark className="w-4 h-4" /> Saved</span>
      <h1 className="font-heading text-3xl md:text-4xl font-bold text-black mb-6">Your saved professions</h1>

      {items.length === 0 ? (
        <div className="nb-card p-8 bg-brand-purple text-center">
          <p className="font-heading text-xl font-bold mb-4">Nothing saved yet</p>
          <button data-testid="go-matches-btn" onClick={() => nav("/results")} className="nb-btn bg-white inline-flex items-center gap-2">
            <Compass className="w-4 h-4" /> View your matches
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <motion.div
              key={it.onet_code}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`saved-card-${it.onet_code}`}
              className="nb-card p-5 flex items-center justify-between gap-4"
            >
              <div>
                <span className="font-heading text-2xl font-bold text-brand-primary">{it.match_score}%</span>
                <h3 className="font-heading text-lg font-bold text-black">{it.title}</h3>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {it.salary && <span className="nb-badge bg-brand-yellow text-xs"><DollarSign className="w-3 h-3" />${(it.salary / 1000).toFixed(0)}k</span>}
                  {it.outlook && <span className="nb-badge bg-brand-secondary text-xs"><TrendingUp className="w-3 h-3" />Outlook</span>}
                </div>
              </div>
              <button
                data-testid={`remove-saved-${it.onet_code}`}
                onClick={() => remove(it.onet_code)}
                className="border-2 border-black rounded-lg p-2 shadow-nb-xs bg-white active:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-all"
                aria-label="Remove"
              >
                <Trash2 className="w-5 h-5 text-brand-primary" />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
